import React, { createContext, useState, ReactNode, useContext, Dispatch, SetStateAction } from 'react';

export interface BankAccount {
    id: number;
    name: string;
    bank: string;
    bankId: number;
    importConfig: string; // JSON string of import configuration
    transactions: {
        count: number;
        firstUsed: string | null;
    };
}

export interface ImportConfig {
    header: boolean;
    amountType: 'separate' | 'single';
    debitColumn?: string | number;
    creditColumn?: string | number;
    amountColumn?: string | number;
    dateColumn: string | number;
    descriptionColumn: string | number;
    dateformat: string;
}

export interface ValidationResult {
    valid: boolean;
    errors: string[];
}

/**
 * Config Guide explaining the import configuration options
 */
export const IMPORT_CONFIG_GUIDE = {
    header: 'Whether the CSV has a header row',
    amountType: 'How amounts are represented: "separate" (debit/credit columns) or "single" (amount column)',
    debitColumn: 'Name of the debit column (required when amountType is "separate")',
    creditColumn: 'Name of the credit column (required when amountType is "separate")',
    amountColumn: 'Name of the amount column (required when amountType is "single")',
    dateColumn: 'Name of the date column (required)',
    descriptionColumn: 'Name of the description column (required)',
    dateformat: 'Format of dates in the CSV (e.g., DD/MM/YYYY, YYYY-MM-DD, DD-MMM-YYYY)',
};

/**
 * Utility functions for parsing and validating CSV data based on import config
 */
export const ImportConfigUtils = {
    /**
     * Get value from row by column reference (name or index)
     * @param row - CSV row as array or object
     * @param column - Column name (string) or index (number)
     * @returns The cell value or empty string
     */
    getRowValue: (row: Record<string, string> | string[], column: string | number): string => {
        if (typeof column === 'number') {
            // Access by index
            return Array.isArray(row) ? (row[column] || '') : '';
        } else {
            // Access by column name
            return typeof row === 'object' && !Array.isArray(row) ? (row[column] || '') : '';
        }
    },

    /**
     * Parse a date string based on the configured date format
     * @param dateStr - The date string from CSV
     * @param dateformat - The date format from config (e.g., 'DD/MM/YYYY')
     * @returns Parsed Date object or null if invalid
     */
    parseDate: (dateStr: string, dateformat: string): Date | null => {
        if (!dateStr || dateStr.trim() === '' || dateStr === '-') {
            return null;
        }

        const trimmedDate = dateStr.trim();
        
        // Common date format patterns
        const formatPatterns: Record<string, RegExp> = {
            'DD/MM/YYYY': /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/,
            'MM/DD/YYYY': /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/,
            'YYYY/MM/DD': /^(\d{4})\/(\d{1,2})\/(\d{1,2})$/,
            'YYYY-MM-DD': /^(\d{4})-(\d{1,2})-(\d{1,2})$/,
            'DD-MM-YYYY': /^(\d{1,2})-(\d{1,2})-(\d{4})$/,
            'DD.MM.YYYY': /^(\d{1,2})\.(\d{1,2})\.(\d{4})$/,
            'DD-MMM-YYYY': /^(\d{1,2})-([A-Za-z]{3})-(\d{4})$/,
            'MMM-DD-YYYY': /^([A-Za-z]{3})-(\d{1,2})-(\d{4})$/,
        };

        const pattern = formatPatterns[dateformat];
        if (!pattern) {
            return null; // Unsupported format
        }

        const match = trimmedDate.match(pattern);
        if (!match) {
            return null;
        }

        let day: number, month: number, year: number;

        // Handle text months
        if (dateformat.includes('MMM')) {
            const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
            const monthStr = match[dateformat.includes('DD-MMM') ? 2 : 1];
            month = monthNames.findIndex(m => m.toLowerCase() === monthStr.toLowerCase()) + 1;
            if (month === 0) return null;

            if (dateformat === 'DD-MMM-YYYY') {
                day = parseInt(match[1], 10);
                year = parseInt(match[3], 10);
            } else {
                day = parseInt(match[2], 10);
                year = parseInt(match[3], 10);
            }
        } else {
            // Numeric month parsing
            if (dateformat === 'DD/MM/YYYY' || dateformat === 'DD-MM-YYYY' || dateformat === 'DD.MM.YYYY') {
                day = parseInt(match[1], 10);
                month = parseInt(match[2], 10);
                year = parseInt(match[3], 10);
            } else if (dateformat === 'MM/DD/YYYY') {
                month = parseInt(match[1], 10);
                day = parseInt(match[2], 10);
                year = parseInt(match[3], 10);
            } else if (dateformat === 'YYYY/MM/DD' || dateformat === 'YYYY-MM-DD') {
                year = parseInt(match[1], 10);
                month = parseInt(match[2], 10);
                day = parseInt(match[3], 10);
            } else {
                return null;
            }
        }

        // Validate date ranges
        if (month < 1 || month > 12 || day < 1 || day > 31) {
            return null;
        }

        // Create date using UTC to avoid timezone offset issues
        const date = new Date(Date.UTC(year, month - 1, day));
        
        // Verify the date is valid (checks for things like Feb 30)
        if (date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day) {
            return null;
        }

        return date;
    },

    /**
     * Parse an amount string (handles both debit/credit and single amount formats)
     * @param row - The CSV row as an object or array
     * @param config - The import config
     * @returns Object with debit and credit amounts, or null if invalid
     */
    parseAmount: (row: Record<string, string> | string[], config: ImportConfig): { debit: number; credit: number } | null => {
        try {
            if (config.amountType === 'separate') {
                const debitStr = ImportConfigUtils.getRowValue(row, config.debitColumn || '')?.trim() || '0';
                const creditStr = ImportConfigUtils.getRowValue(row, config.creditColumn || '')?.trim() || '0';

                const debit = parseFloat(debitStr) || 0;
                const credit = parseFloat(creditStr) || 0;

                if (isNaN(debit) || isNaN(credit)) {
                    return null;
                }

                return { debit, credit };
            } else {
                const amountStr = ImportConfigUtils.getRowValue(row, config.amountColumn || '')?.trim();
                if (!amountStr) return null;

                const amount = parseFloat(amountStr);
                if (isNaN(amount)) {
                    return null;
                }

                // Determine if debit or credit (positive = credit, negative = debit)
                return amount < 0
                    ? { debit: Math.abs(amount), credit: 0 }
                    : { debit: 0, credit: amount };
            }
        } catch (error) {
            return null;
        }
    },

    /**
     * Validate that a CSV row has all required columns and valid data
     * @param row - The CSV row as an object or array
     * @param config - The import config
     * @returns ValidationResult with errors if any
     */
    validateRow: (row: Record<string, string> | string[], config: ImportConfig): ValidationResult => {
        const errors: string[] = [];

        // For array-based rows (when header is false), check array length
        if (Array.isArray(row)) {
            const maxIndex = Math.max(
                typeof config.dateColumn === 'number' ? config.dateColumn : -1,
                typeof config.descriptionColumn === 'number' ? config.descriptionColumn : -1,
                typeof config.debitColumn === 'number' ? config.debitColumn : -1,
                typeof config.creditColumn === 'number' ? config.creditColumn : -1,
                typeof config.amountColumn === 'number' ? config.amountColumn : -1,
            );
            if (row.length <= maxIndex) {
                errors.push(`Row has insufficient columns (expected at least ${maxIndex + 1})`);
            }
        } else {
            // For object-based rows (when header is true), check column names
            if (typeof config.dateColumn === 'string' && !row.hasOwnProperty(config.dateColumn)) {
                errors.push(`Missing required column: "${config.dateColumn}"`);
            }
            if (typeof config.descriptionColumn === 'string' && !row.hasOwnProperty(config.descriptionColumn)) {
                errors.push(`Missing required column: "${config.descriptionColumn}"`);
            }

            if (config.amountType === 'separate') {
                if (typeof config.debitColumn === 'string' && !row.hasOwnProperty(config.debitColumn)) {
                    errors.push(`Missing required column: "${config.debitColumn}"`);
                }
                if (typeof config.creditColumn === 'string' && !row.hasOwnProperty(config.creditColumn)) {
                    errors.push(`Missing required column: "${config.creditColumn}"`);
                }
            } else {
                if (typeof config.amountColumn === 'string' && !row.hasOwnProperty(config.amountColumn)) {
                    errors.push(`Missing required column: "${config.amountColumn}"`);
                }
            }
        }

        // Validate date
        const dateStr = ImportConfigUtils.getRowValue(row, config.dateColumn)?.trim();
        if (dateStr && dateStr !== '-') {
            const parsedDate = ImportConfigUtils.parseDate(dateStr, config.dateformat);
            if (!parsedDate) {
                errors.push(`Invalid date format for "${dateStr}". Expected format: ${config.dateformat}`);
            } else {
                // A statement can't hold transactions that haven't happened yet.
                // parseDate gives UTC midnight; compared with today's local date.
                const now = new Date();
                if (parsedDate.getTime() > Date.UTC(now.getFullYear(), now.getMonth(), now.getDate())) {
                    errors.push(`Date "${dateStr}" is in the future`);
                }
            }
        } else {
            errors.push(`Date field is empty`);
        }

        // Validate amount
        const amountValidation = ImportConfigUtils.parseAmount(row, config);
        if (!amountValidation) {
            errors.push(`Invalid amount format`);
        } else if (amountValidation.debit === 0 && amountValidation.credit === 0) {
            errors.push(`Amount cannot be zero`);
        }

        // Validate description is not empty
        const description = ImportConfigUtils.getRowValue(row, config.descriptionColumn)?.trim();
        if (!description) {
            errors.push(`Description field is empty`);
        }

        return {
            valid: errors.length === 0,
            errors,
        };
    },

    /**
     * Validate that CSV headers match the expected columns from config
     * @param headers - Array of column headers from CSV
     * @param config - The import config
     * @returns ValidationResult with errors if any
     */
    validateHeaders: (headers: string[], config: ImportConfig): ValidationResult => {
        const errors: string[] = [];

        // If config uses numeric indices, validate array length instead
        if (typeof config.dateColumn === 'number' || typeof config.descriptionColumn === 'number') {
            const maxIndex = Math.max(
                typeof config.dateColumn === 'number' ? config.dateColumn : -1,
                typeof config.descriptionColumn === 'number' ? config.descriptionColumn : -1,
                typeof config.debitColumn === 'number' ? config.debitColumn : -1,
                typeof config.creditColumn === 'number' ? config.creditColumn : -1,
                typeof config.amountColumn === 'number' ? config.amountColumn : -1,
            );
            if (headers.length <= maxIndex) {
                errors.push(`CSV has insufficient columns (expected at least ${maxIndex + 1})`);
            }
            return {
                valid: errors.length === 0,
                errors,
            };
        }

        // Check string-based column names
        const requiredColumns: string[] = [
            config.dateColumn as string,
            config.descriptionColumn as string,
        ];

        if (config.amountType === 'separate') {
            if (config.debitColumn) requiredColumns.push(config.debitColumn as string);
            if (config.creditColumn) requiredColumns.push(config.creditColumn as string);
        } else {
            if (config.amountColumn) requiredColumns.push(config.amountColumn as string);
        }

        const headerSet = new Set(headers);

        for (const col of requiredColumns) {
            if (col && !headerSet.has(col)) {
                errors.push(`Missing expected column: "${col}"`);
            }
        }

        return {
            valid: errors.length === 0,
            errors,
        };
    },

    /**
     * Transform a raw CSV row into a standardized transaction object
     * @param row - The CSV row as an object or array
     * @param config - The import config
     * @returns Standardized transaction object or null if invalid
     */
    transformRow: (row: Record<string, string> | string[], config: ImportConfig) => {
        const validation = ImportConfigUtils.validateRow(row, config);
        if (!validation.valid) {
            return null;
        }

        const dateStr = ImportConfigUtils.getRowValue(row, config.dateColumn);
        const parsedDate = ImportConfigUtils.parseDate(dateStr, config.dateformat);
        const amountData = ImportConfigUtils.parseAmount(row, config);

        if (!parsedDate || !amountData) {
            return null;
        }

        const amount = amountData.debit > 0 ? -amountData.debit : amountData.credit;

        return {
            date: parsedDate,
            description: ImportConfigUtils.getRowValue(row, config.descriptionColumn).trim(),
            amount,
            // The original row exactly as parsed from the CSV (every column, not
            // just the ones mapped above) - preserved end-to-end so it can be
            // stored per-transaction rather than discarded once mapping is done.
            sourceData: row,
        };
    },
};

interface BankContextType {
    bankAccounts: BankAccount[];
    setBankAccounts: Dispatch<SetStateAction<BankAccount[]>>;
    selectedBankAccount: BankAccount | null;
    setSelectedBankAccount: Dispatch<SetStateAction<BankAccount | null>>;
    selectedImportConfig: ImportConfig | null;
    setSelectedImportConfig: Dispatch<SetStateAction<ImportConfig | null>>;
}

export const BankContext = createContext<BankContextType | undefined>(undefined);

interface BankProviderProps {
    children: ReactNode;
}

export const BankProvider: React.FC<BankProviderProps> = ({ children }) => {
    const [bankAccounts, setBankAccounts] = useState<BankAccount[]>([]);
    const [selectedBankAccount, setSelectedBankAccount] = useState<BankAccount | null>(null);
    const [selectedImportConfig, setSelectedImportConfig] = useState<ImportConfig | null>(null);

    return (
        <BankContext.Provider
            value={{
                bankAccounts,
                setBankAccounts,
                selectedBankAccount,
                setSelectedBankAccount,
                selectedImportConfig,
                setSelectedImportConfig,
            }}
        >
            {children}
        </BankContext.Provider>
    );
};

export const useBank = () => {
    const context = useContext(BankContext);
    if (!context) {
        throw new Error('useBank must be used within a BankProvider');
    }
    return context;
};
